import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppPages } from '@app/app.pages';
import { AuthState } from '@app/enum/auth-state.enum';
import { JwtToken } from '@app/interfaces/jwt-payload.interface';
import { User } from '@app/models/user';
import { environment } from '@environment/environment';
import { AnalyticsService } from '@services/analytics/analytics.service';
import { GroupsService } from '@services/groups/groups.service';
import { isExpectedOAuthTokenError } from '@shared/utils/sentry-event-filter.util';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, filter, firstValueFrom, interval, map, Observable, takeUntil } from 'rxjs';

export const authConfig: AuthConfig = environment.authConfig;

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly authStatus = new BehaviorSubject<AuthState>(AuthState.Pending);
  private readonly groupsService = inject(GroupsService);
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly initialization: Promise<void>;
  private readonly oauthService: OAuthService = inject(OAuthService);
  private readonly router: Router = inject(Router);
  private isRefreshingToken = false;

  constructor() {
    this.initialization = this.initialize();
  }

  /*
   * Getter method to return the current authentication state
   */
  public isAuthenticated(): Observable<AuthState> {
    return this.authStatus.asObservable();
  }

  /*
   * Will be called on application startup and initialize the OAuthService
   */
  public async initialize(): Promise<void> {
    this.oauthService.setStorage(localStorage);
    this.oauthService.configure(authConfig);
    this.oauthService.stopAutomaticRefresh();

    try {
      await this.oauthService.loadDiscoveryDocumentAndTryLogin();
    } catch (error: unknown) {
      if (isExpectedOAuthTokenError(error)) {
        this.oauthService.logOut(false);
      } else {
        throw error;
      }
    }

    this.authStatus.next(
      this.oauthService.hasValidAccessToken() ? AuthState.Authenticated : AuthState.NotAuthenticated
    );

    if (this.oauthService.hasValidAccessToken()) {
      await this.initializeAnalyticsUserContext();
    }

    this.setupAutomaticSilentRefresh();
  }

  /*
   * Getter method to return, if the user is authenticated
   */
  public async hasValidAccessToken(): Promise<boolean> {
    await this.initialization;
    return this.oauthService.hasValidAccessToken();
  }

  /*
   * Method is used to login the user
   */
  public async login(): Promise<void> {
    this.oauthService.initCodeFlow();
    this.setupAutomaticSilentRefresh();
  }

  /*
   * Method is used to retrieve the current user's details from the JWT token
   */
  public getUserDetails(): User {
    const token = this.getAccessToken();
    return {
      name: token?.name || 'TEST USER',
      id: token?.wuero_customerid || '123456',
      department: token?.wuero_department || 'TEST ABTEILUNG',
      groups: this.getPermissionGroups()
    };
  }

  /*
   * Method is used to retrieve the current user's permission groups from the JWT token
   */
  public getPermissionGroups(): string[] {
    // mock data for local development
    if (!environment.production)
      return ['infwb_ad-f_bnk_pden', 'infwb_ad-f', 'infwb_ad-f_bnk', 'infwb_ad-a', 'infwb_id-v', 'infwb_id-z'];
    // if (!environment.production) return ['infwb_ad-f_bnk_pden', 'infwb_ad-f', 'infwb_ad-f_bnk'];
    try {
      const token = this.getAccessToken();
      console.log('🚀 ~ AuthService ~ getPermissionGroups ~ token.wuero_groups:', token);
      const groups = token.wuero_groups.filter((group: string) => group.startsWith('infwb'));
      console.log('🚀 ~ AuthService ~ getPermissionGroups ~ filtered groups:', groups);
      return groups;
    } catch (error: unknown) {
      console.log('🚀 ~ file: auth.service.ts:43 ~ AuthService ~ getPermissionGroups ~ error:', error);
      return [];
    }
  }

  /*
   * Method is used to logout the user
   * Custom logout method to clear local storage and cookies
   * POST request to the Keycloak logout endpoint
   * GET call for development
   */
  public logout(): void {
    this.analyticsService.clearUserContext();

    if (environment.production) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      });

      const body = new URLSearchParams();
      body.set('client_id', 'pis');
      body.set('refresh_token', this.oauthService.getRefreshToken());

      this.httpClient
        .post(authConfig.logoutUrl ?? '', body.toString(), {
          headers
        })
        .subscribe({
          next: v => console.log(v),
          error: e => console.error(e),
          complete: () => {
            console.log('Logout successful');
            // clear local storage
            if (localStorage) {
              localStorage.clear();
            }
            // clear cookies
            if (document && document.cookie) {
              document.cookie.split(';').forEach((c: string) => {
                const name = c.split('=')[0].trim();
                if (name !== 'directus_refresh_token') {
                  document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=' + location.hostname;
                }
              });
            }
            // reload the page
            this.authStatus.next(AuthState.NotAuthenticated);
            this.router.navigate([AppPages.Login]);
          }
        });
    } else {
      this.oauthService.logOut(false);
    }
  }

  /**
   * Resolve Directus group titles for the current user's SSO groups and set Matomo visit context.
   */
  private async initializeAnalyticsUserContext(): Promise<void> {
    try {
      const titles = await firstValueFrom(this.groupsService.resolveTitles(this.getPermissionGroups()));
      if (titles.length > 0) {
        this.analyticsService.initializeUserContext(titles.join(', '));
      }
    } catch {
      // Skip analytics dimension on failure — never block auth
    }
  }

  /*
   * Method is used to retrieve the current access token of the user
   */
  private getAccessToken(): JwtToken {
    try {
      return jwtDecode(this.oauthService.getAccessToken()) as JwtToken;
    } catch (error: unknown) {
      console.log('🚀 ~ file: auth.service.ts:58 ~ AuthService ~ getAccessToken ~ error:', error);
      try {
        return jwtDecode(this.oauthService.getIdToken()) as JwtToken;
      } catch (error: unknown) {
        console.log('🚀 ~ file: auth.service.ts:61 ~ AuthService ~ getAccessToken ~ error:', error);
        return {} as JwtToken;
      }
    }
  }

  /*
   * Method is used to setup the automatic silent refresh of the access token
   */
  private setupAutomaticSilentRefresh(): void {
    if (this.oauthService.hasValidAccessToken()) {
      this.startTokenRefreshInterval();
    }
  }

  /*
   * Custom method to refresh the access token, because the setupAutomaticSilentRefresh angular-oauth2-oidc library does not work currently
   */
  private startTokenRefreshInterval(): void {
    // for testing call it once after 5 seconds
    if (!environment.production) {
      setTimeout(() => {
        this.refreshTokenSafely();
      }, 5000);
    }
    // Check the token every minute
    interval(60000)
      .pipe(
        // Stop the interval when the user logs out
        takeUntil(this.authStatus.pipe(filter(status => status === AuthState.NotAuthenticated))),
        // Get the token's expiration time
        map(() => this.getExpirationTime()),
        // If the token is about to expire in the next 2 minutes, refresh it
        filter(expirationTime => (expirationTime ? Date.now() + 60000 >= expirationTime : false))
      )
      .subscribe(() => this.refreshTokenSafely());
  }

  /*
   * Safely refresh the token with error handling for expired refresh tokens
   * Prevents Sentry errors for expected token expiration scenarios
   */
  private refreshTokenSafely(): void {
    if (this.isRefreshingToken) {
      return;
    }

    if (!this.oauthService.getRefreshToken()) {
      this.handleTokenExpiration();
      return;
    }

    this.isRefreshingToken = true;

    void this.oauthService
      .refreshToken()
      .then(() => {
        this.isRefreshingToken = false;
      })
      .catch((error: unknown) => {
        this.isRefreshingToken = false;

        if (isExpectedOAuthTokenError(error)) {
          this.handleTokenExpiration();
          return;
        }

        console.warn('Token refresh failed:', error);
        this.handleTokenExpiration();
      });
  }

  /*
   * Handle token expiration by updating auth state and stopping refresh attempts
   */
  private handleTokenExpiration(): void {
    this.analyticsService.clearUserContext();
    this.authStatus.next(AuthState.NotAuthenticated);
    // Clear tokens from storage
    this.oauthService.logOut(false);
  }

  /*
   * Method is used to retrieve the expiration time of the current access token
   */
  private getExpirationTime(): number | null {
    const token = this.getAccessToken();
    if (!token) return null;

    const time = token.exp ? token.exp * 1000 : null;
    return time;
  }
}
